// ** React Imports
import { createContext, useContext, useState, useCallback } from 'react';

/**
 * AuthModalContext
 * 
 * Global context to manage login and signup modal state
 * Used for public users who need to authenticate via popup modals
 * 
 * Features:
 * - Login modal state management
 * - Signup modal state management
 * - Callback to resume action after successful login
 */

const AuthModalContext = createContext({
  isLoginModalOpen: false,
  isSignupModalOpen: false,
  openLoginModal: () => {},
  openSignupModal: () => {},
  closeModals: () => {},
  switchToSignup: () => {},
  switchToLogin: () => {},
  setOnLoginSuccess: () => {},
});

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
};

export const AuthModalProvider = ({ children }) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [onLoginSuccessCallback, setOnLoginSuccessCallback] = useState(null);

  /**
   * Open login modal
   * @param {Function} onSuccess - Optional callback to execute after successful login
   */
  const openLoginModal = useCallback((onSuccess = null) => {
    setIsLoginModalOpen(true);
    setIsSignupModalOpen(false);
    if (onSuccess) {
      setOnLoginSuccessCallback(() => onSuccess);
    }
  }, []);

  /**
   * Open signup modal
   * @param {Function} onSuccess - Optional callback to execute after successful signup
   */
  const openSignupModal = useCallback((onSuccess = null) => {
    setIsSignupModalOpen(true);
    setIsLoginModalOpen(false);
    if (onSuccess) {
      setOnLoginSuccessCallback(() => onSuccess);
    }
  }, []);

  /**
   * Close all auth modals
   */
  const closeModals = useCallback(() => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(false);
    setOnLoginSuccessCallback(null);
  }, []);

  /**
   * Switch from login to signup modal
   */
  const switchToSignup = useCallback(() => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(true);
  }, []);

  /**
   * Switch from signup to login modal
   */
  const switchToLogin = useCallback(() => {
    setIsSignupModalOpen(false);
    setIsLoginModalOpen(true);
  }, []);

  /**
   * Set callback for successful login/signup
   */
  const setOnLoginSuccess = useCallback((callback) => {
    setOnLoginSuccessCallback(() => callback);
  }, []);

  /**
   * Execute success callback if exists
   */
  const executeSuccessCallback = useCallback(() => {
    if (onLoginSuccessCallback) {
      onLoginSuccessCallback();
      setOnLoginSuccessCallback(null);
    }
  }, [onLoginSuccessCallback]);

  const value = {
    isLoginModalOpen,
    isSignupModalOpen,
    openLoginModal,
    openSignupModal,
    closeModals,
    switchToSignup,
    switchToLogin,
    setOnLoginSuccess,
    executeSuccessCallback,
    onLoginSuccessCallback,
  };

  return (
    <AuthModalContext.Provider value={value}>
      {children}
    </AuthModalContext.Provider>
  );
};

export default AuthModalContext;


